import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import VideoService from "../Services/VideoPlayer";
import YouTube from "react-youtube";
import { useVideo } from "../Contexts/VideosContext";
import { t } from "i18next";
import AuthorsService from "../Services/Authors";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import { pageStyles } from "../Contexts/VideosContext";
import Favfill from "../assets/img/favadd.svg";
import Favempty from "../assets/img/fav.svg";
import gitasevalogo from "../../src/Images/logoGST.png";
import { Avatar, Button } from "@mui/material";
import VideoSeo from "./VideoSeo";
import Loader from "./Loader";
import VideoLandingSkeleton from "./Skeleton/VideoLandingSkeleton";
import { LogInModel } from "./LoginModel";

const normalizeSlug = (slug?: string) => {
  if (!slug) return "";

  try {
    return decodeURIComponent(slug).trim();
  } catch {
    return slug.trim();
  }
};

const normalizeId = (value: any) =>
  value === null || value === undefined ? "" : String(value).trim();

const uniqueValues = (values: string[]) =>
  Array.from(new Set(values.filter(Boolean)));

const asArray = (value: any): any[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const RELATED_VIDEO_LIMIT = 15;

const getEntityId = (item: any) =>
  normalizeId(item?.id ?? item?._id ?? item?.value ?? item?.key ?? item?.categoryId ?? item?.authorId);

const getEntityName = (item: any) =>
  normalizeId(
    item?.name ??
    item?.title ??
    item?.label ??
    item?.text ??
    item?.categoryName ??
    item?.authorName
  );

const uniqueEntities = (items: any[]) => {
  const seen = new Set<string>();

  return items.filter((item) => {
    if (!item) return false;

    const name = getEntityName(item).toLowerCase();
    const id = getEntityId(item).toLowerCase();
    const key = name || id || JSON.stringify(item);

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeCompareValue = (value: any) =>
  normalizeId(value)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const getVideoTitle = (video: any) =>
  normalizeId(video?.name ?? video?.title ?? video?.videoName);

const inferVideoSeriesName = (video: any) => {
  const title = getVideoTitle(video);
  if (!title) return "";

  return title
    .replace(/^\s*दिवस\s*\d+\s*\/\s*\d+\s*[:：-]\s*/i, "")
    .replace(/^\s*day\s*\d+\s*\/\s*\d+\s*[:：-]\s*/i, "")
    .replace(/\s*\(\s*\d+\s*\/\s*\d+\s*\)\s*$/i, "")
    .replace(/\s*[-–—]\s*\d+\s*\/\s*\d+\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
};

const collectEntityIds: any = (...values: any[]) =>
  uniqueValues(
    values.flatMap((value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.flatMap((item) => collectEntityIds(item));
      if (typeof value === "object") return [getEntityId(value)];
      return [normalizeId(value)];
    })
  );

const collectEntityNames: any = (...values: any[]) =>
  uniqueValues(
    values.flatMap((value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.flatMap((item) => collectEntityNames(item));
      if (typeof value === "object") return [getEntityName(value)];
      return [normalizeId(value)];
    })
  );

const getVideoId = (video: any) => {
  if (!video) return null;
  return video.videoHash ? video.videoHash : video.categoryHash || null;
};

const getVideoDbId = (video: any) =>
  normalizeId(video?.id ?? video?._id ?? video?.videoId ?? video?.videoID);

const getVideoCategoryObjects = (video: any) => {
  if (!video) return [];

  const categoryObjects = [
    ...asArray(video.category),
    ...asArray(video.categories),
    ...asArray(video.videoCategory),
    ...asArray(video.videoCategories),
  ].filter((item) => item && typeof item === "object");

  const categoryNames = [
    video.categoryName,
    video.videoCategoryName,
    typeof video.category === "string" ? video.category : "",
    inferVideoSeriesName(video),
  ].filter(Boolean);

  return uniqueEntities([
    ...categoryObjects,
    ...categoryNames.map((name) => ({ id: name, name })),
  ]);
};

const getVideoAuthorObjects = (video: any) => {
  if (!video) return [];

  const authorObjects = [
    ...asArray(video.author),
    ...asArray(video.authors),
    ...asArray(video.singer),
    ...asArray(video.singers),
    ...asArray(video.vachak),
    ...asArray(video.vachaks),
  ].filter((item) => item && typeof item === "object");

  const authorNames = [
    video.authorName,
    video.singerName,
    video.vachakName,
    typeof video.author === "string" ? video.author : "",
    typeof video.singer === "string" ? video.singer : "",
    typeof video.vachak === "string" ? video.vachak : "",
  ].filter(Boolean);

  return uniqueEntities([
    ...authorObjects,
    ...authorNames.map((name) => ({ id: name, name })),
  ]);
};

const getVideoCategoryIds = (video: any) =>
  collectEntityIds(
    video?.categoryId,
    video?.categoryID,
    video?.category_id,
    video?.videoCategoryId,
    video?.parentCategoryId,
    video?.categoryIds,
    video?.categoriesIds,
    video?.category,
    video?.categories,
    video?.videoCategory,
    video?.videoCategories
  );

const getVideoAuthorIds = (video: any) =>
  collectEntityIds(
    video?.authorId,
    video?.authorID,
    video?.author_id,
    video?.singerId,
    video?.vachakId,
    video?.authorIds,
    video?.authorsIds,
    video?.author,
    video?.authors,
    video?.singer,
    video?.singers,
    video?.vachak,
    video?.vachaks
  );

const getVideoCategoryNames = (video: any) =>
  collectEntityNames(
    video?.categoryName,
    video?.videoCategoryName,
    video?.category,
    video?.categories,
    video?.videoCategory,
    video?.videoCategories,
    inferVideoSeriesName(video)
  );

const getVideoAuthorNames = (video: any) =>
  collectEntityNames(
    video?.authorName,
    video?.singerName,
    video?.vachakName,
    video?.author,
    video?.authors,
    video?.singer,
    video?.singers,
    video?.vachak,
    video?.vachaks
  );

const entityMatches = (item: any, ids: string[], names: string[]) => {
  const itemId = normalizeCompareValue(getEntityId(item));
  const itemName = normalizeCompareValue(getEntityName(item));
  const normalizedIds = ids.map(normalizeCompareValue);
  const normalizedNames = names.map(normalizeCompareValue);

  return (
    Boolean(itemId && normalizedIds.includes(itemId)) ||
    Boolean(itemName && normalizedNames.includes(itemName))
  );
};

const flattenCategories = (items: any[]): any[] =>
  items.flatMap((item) => [
    item,
    ...flattenCategories(asArray(item?.items)),
    ...flattenCategories(asArray(item?.children)),
    ...flattenCategories(asArray(item?.subCategories)),
  ]);

const isSameVideo = (a: any, b: any) => {
  const aId = getVideoDbId(a);
  const bId = getVideoDbId(b);
  const aSlug = normalizeSlug(a?.slug);
  const bSlug = normalizeSlug(b?.slug);

  return Boolean(
    (aId && bId && aId === bId) ||
    (aSlug && bSlug && aSlug === bSlug) ||
    (getVideoId(a) && getVideoId(a) === getVideoId(b))
  );
};

const getCategoryCompareValues = (video: any) =>
  uniqueValues([
    ...getVideoCategoryIds(video),
    ...getVideoCategoryNames(video),
    inferVideoSeriesName(video),
  ]).map(normalizeCompareValue);

const videoMatchesCategory = (
  video: any,
  categoryIds: string[],
  categoryNames: string[],
  currentVideo: any
) => {
  const wantedCategoryValues = uniqueValues([
    ...categoryIds,
    ...categoryNames,
    inferVideoSeriesName(currentVideo),
  ])
    .map(normalizeCompareValue)
    .filter(Boolean);

  if (!wantedCategoryValues.length) return false;

  const videoCategoryValues = getCategoryCompareValues(video);
  return videoCategoryValues.some((value) => wantedCategoryValues.includes(value));
};

const videoMatchesAuthor = (
  video: any,
  authorIds: string[],
  authorNames: string[]
) => {
  const wantedAuthorValues = uniqueValues([...authorIds, ...authorNames])
    .map(normalizeCompareValue)
    .filter(Boolean);

  if (!wantedAuthorValues.length) return true;

  const videoAuthorValues = uniqueValues([
    ...getVideoAuthorIds(video),
    ...getVideoAuthorNames(video),
  ]).map(normalizeCompareValue);

  return videoAuthorValues.some((value) => wantedAuthorValues.includes(value));
};

const getNextRelatedVideos = (items: any[], currentVideo: any) => {
  return items
    .filter((video) => !isSameVideo(video, currentVideo))
    .slice(0, RELATED_VIDEO_LIMIT);
};



const VideoLandingPage = () => {
  const params = useParams<{ slug?: string }>();
  const [logIn, setLogin] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const routeState = location.state as {
    video?: any;
    videoId?: string;
    youtubeId?: string;
    categoryId?: string;
    categoryName?: string;
    authorId?: string;
  } | null;

  const [videos, setVideos] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any | null>(null);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const { refVideo, playbackTime,
    setPlaybackTime,
    videoObjId,
    setVideoObjId,
    likedMsg,
    setLikedMsg,
    setIsLiked,
    isLiked,
    activeMedia,
    setActiveMedia } = useVideo();
  const [authors, setAuthors] = useState<any[]>([]);
  const routeSlug = useMemo(() => normalizeSlug(params.slug), [params.slug]);
  const [thumbUrls, setThumbUrls] = useState<any[]>([]);
  const [categoryId, setCategoryId] = useState<any>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [allVideos, setAllVideos] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [sort, setSort] = useState("0");
  const [authorId, setAuthorId] = useState<any>(null);

  const youtubeOptions = useMemo(
    () => ({
      height: "100%",
      width: "100%",
      playerVars: {
        autoplay: 1,
        mute: 1,
        playsinline: 1,
        controls: 1,
        rel: 0,
        modestbranding: 1,
      },
    }),
    []
  );

  const closeModal = () => {
    setLogin(false);
  };
  // alert(playingVideoId)
  const heroVideo = useMemo(
    () => selectedVideo || videos[0] || null,
    [selectedVideo, videos]
  );
  useEffect(() => {
    if (!heroVideo) return;

    setVideoObjId(heroVideo.id);

    // API se jo favourite flag aa raha ho uske according
    setIsLiked(
      heroVideo.isFavourite ||
      heroVideo.isFavorite ||
      heroVideo.isLiked ||
      false
    );
  }, [heroVideo]);
  const selectedCategoryIds = useMemo(
    () => uniqueValues([normalizeId(categoryId), ...getVideoCategoryIds(heroVideo)]),
    [categoryId, heroVideo]
  );

  const selectedCategoryNames = useMemo(
    () =>
      uniqueValues([
        selectedCategoryName,
        ...getVideoCategoryNames(heroVideo),
        inferVideoSeriesName(heroVideo),
      ]),
    [heroVideo, selectedCategoryName]
  );

  const selectedAuthorIds = useMemo(
    () => uniqueValues([normalizeId(authorId), ...getVideoAuthorIds(heroVideo)]),
    [authorId, heroVideo]
  );

  const selectedAuthorNames = useMemo(
    () => getVideoAuthorNames(heroVideo),
    [heroVideo]
  );

  const relatedAuthors = useMemo(() => {
    const matchedAuthors = authors.filter((author) =>
      entityMatches(author, selectedAuthorIds, selectedAuthorNames)
    );

    return uniqueEntities([
      ...matchedAuthors,
      ...getVideoAuthorObjects(heroVideo),
    ]);
  }, [authors, heroVideo, selectedAuthorIds, selectedAuthorNames]);

  const relatedCategories = useMemo(() => {
    const flatCategories = flattenCategories(thumbUrls);
    const matchedCategories = flatCategories.filter((category) =>
      entityMatches(category, selectedCategoryIds, selectedCategoryNames)
    );

    return uniqueEntities([
      ...matchedCategories,
      ...getVideoCategoryObjects(heroVideo),
    ]);
  }, [heroVideo, selectedCategoryIds, selectedCategoryNames, thumbUrls]);

  const getThumbnail = (video: any) => {
    if (!video) return "https://img.youtube.com/vi/qI_y3z0kcxk/maxresdefault.jpg";
    if (video.videoHash) return `https://img.youtube.com/vi/${video.videoHash}/hqdefault.jpg`;
    if (video.categoryHash) return `https://img.youtube.com/vi/${video.categoryHash}/hqdefault.jpg`;
    return "https://img.youtube.com/vi/qI_y3z0kcxk/maxresdefault.jpg";
  };

  const loadVideosForContext = async (
    nextCategoryId?: any,
    nextAuthorId?: any,
    currentVideo?: any,
    nextCategoryName?: any,
    nextAuthorName?: any
  ) => {
    const normalizedCategoryId = normalizeId(nextCategoryId);
    const normalizedAuthorId = normalizeId(nextAuthorId);
    const explicitCategoryName = normalizeId(nextCategoryName);
    const categoryIdLooksLikeName =
      normalizedCategoryId &&
      explicitCategoryName &&
      normalizeCompareValue(normalizedCategoryId) ===
      normalizeCompareValue(explicitCategoryName);
    const apiCategoryId = categoryIdLooksLikeName ? "" : normalizedCategoryId;
    const categoryIds = uniqueValues([
      apiCategoryId,
      ...getVideoCategoryIds(currentVideo),
    ]);
    const categoryNames = uniqueValues([
      explicitCategoryName,
      categoryIdLooksLikeName ? normalizedCategoryId : "",
      ...getVideoCategoryNames(currentVideo),
      inferVideoSeriesName(currentVideo),
    ]);
    const authorIds = normalizedAuthorId ? [normalizedAuthorId] : [];
    const authorNames = normalizeId(nextAuthorName)
      ? [normalizeId(nextAuthorName)]
      : [];

    if (!categoryIds.length && !categoryNames.length) {
      setVideos([]);
      setAllVideos([]);
      setTotalRecords(0);
      return [];
    }

    const response = await VideoService.getAllVideos(
      apiCategoryId,
      normalizedAuthorId,
      sort,
      500
    );

    const items = response?.result?.items ?? [];

    // const matchingItems = items
    //   .filter((video: any) =>
    //     videoMatchesCategory(video, categoryIds, categoryNames, currentVideo)
    //   )
    //   .filter((video: any) => videoMatchesAuthor(video, authorIds, authorNames));

    const matchingItems = items
      .filter((video: any) =>
        videoMatchesCategory(video, categoryIds, categoryNames, currentVideo)
      )
      .filter((video: any) =>
        videoMatchesAuthor(video, authorIds, authorNames)
      );
    // const relatedItems = getNextRelatedVideos(matchingItems, currentVideo);

    // setVideos(relatedItems);
    // setAllVideos(relatedItems);
    // setTotalRecords(relatedItems.length);

    // return relatedItems;

    const currentIndex = matchingItems.findIndex((video: any) =>
      isSameVideo(video, currentVideo)
    );

    let orderedVideos: any[] = [];

    if (currentIndex >= 0) {
      const afterVideos = matchingItems.slice(currentIndex + 1);
      const beforeVideos = matchingItems.slice(0, currentIndex);

      orderedVideos = [...afterVideos, ...beforeVideos];
    } else {
      orderedVideos = matchingItems.filter(
        (video: any) => !isSameVideo(video, currentVideo)
      );
    }

    const relatedItems = orderedVideos.slice(0, RELATED_VIDEO_LIMIT);

    setVideos(relatedItems);
    setAllVideos(relatedItems);
    setTotalRecords(relatedItems.length);

    return relatedItems;
  };

  // useEffect(() => {
  //   const fetchVideos = async () => {
  //     setLoading(true);

  //     try {
  //       let allItems: any[] = [];
  //       let matchedVideo = routeState?.video || null;

  //       if (!matchedVideo) {
  //         const response = await VideoService.getAllVideos("", "", "0", 500);
  //         allItems = response?.result?.items ?? [];

  //         matchedVideo =
  //           allItems.find(
  //             (video: any) =>
  //               normalizeSlug(video.slug) === routeSlug ||
  //               getVideoDbId(video) === routeSlug
  //           ) ||
  //           allItems[0] ||
  //           null;
  //       }

  //       if (!matchedVideo) {
  //         setVideos([]);
  //         setAllVideos([]);
  //         setTotalRecords(0);
  //         setSelectedVideo(null);
  //         setPlayingVideoId(null);
  //         return;
  //       }

  //       const matchedCategoryId =
  //         normalizeId(routeState?.categoryId) ||
  //         getVideoCategoryIds(matchedVideo)[0] ||
  //         "";
  //       const matchedCategoryName =
  //         normalizeId(routeState?.categoryName) ||
  //         getVideoCategoryNames(matchedVideo)[0] ||
  //         inferVideoSeriesName(matchedVideo) ||
  //         "";

  //       const matchedAuthorId =
  //         normalizeId(routeState?.authorId) ||
  //         getVideoAuthorIds(matchedVideo)[0] ||
  //         "";

  //       await loadVideosForContext(
  //         matchedCategoryId,
  //         "",
  //         matchedVideo,
  //         matchedCategoryName
  //       );

  //       setSelectedVideo(matchedVideo);
  //       setPlayingVideoId(getVideoId(matchedVideo));
  //       setCategoryId(matchedCategoryId || null);
  //       setSelectedCategoryName(matchedCategoryName || "");
  //       setAuthorId(matchedAuthorId || null);
  //     } catch (error) {
  //       console.error("Error loading videos for landing page:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchVideos();
  // }, [routeSlug, location.state, sort, i18n.language]);
  const [description, setDescription] = useState({
    __html: "",
  });


  const [videosDetail, setVideosDetail] = useState<any>();
  const handleLoginStateChange = () => {

  }
  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);

      try {
        let videoRes: any = null;

        const prefetched = videosDetail;

        if (prefetched) {
          videoRes = {
            status: true,
            result: prefetched,
          };
        } else {
          try {
            videoRes = await VideoService.getVideoDetails(videoObjId);
          } catch (error: any) {
            console.warn(
              "getVideoDetails API not available in this environment",
              error
            );

            videoRes = {
              status: false,
              result: {
                description: "",
                isFavourite: false,
              },
            };
          }
        }

        if (videoRes?.status) {
          setDescription({
            __html: videoRes?.result?.description || "",
          });

          setVideosDetail(videoRes?.result);
          setIsLiked(videoRes?.result?.isFavourite || false);
        }

        let allItems: any[] = [];
        let matchedVideo = selectedVideo || routeState?.video || null;

        const response = await VideoService.getAllVideos("", "", "0", 500);
        allItems = response?.result?.items ?? [];

        if (!matchedVideo) {
          matchedVideo =
            allItems.find(
              (video: any) =>
                normalizeSlug(video.slug) === routeSlug ||
                getVideoDbId(video) === routeSlug
            ) ||
            allItems[0] ||
            null;
        } else {
          const updatedVideo = allItems.find(
            (v: any) => getVideoDbId(v) === getVideoDbId(matchedVideo)
          );

          if (updatedVideo) {
            matchedVideo = updatedVideo;
          }
        }

        if (!matchedVideo) {
          setVideos([]);
          setAllVideos([]);
          setTotalRecords(0);
          setSelectedVideo(null);
          setPlayingVideoId(null);
          return;
        }

        const matchedCategoryId =
          getVideoCategoryIds(matchedVideo)[0] ||
          normalizeId(routeState?.categoryId) ||
          "";

        const matchedCategoryName =
          getVideoCategoryNames(matchedVideo)[0] ||
          inferVideoSeriesName(matchedVideo) ||
          normalizeId(routeState?.categoryName) ||
          "";

        const matchedAuthorId =
          getVideoAuthorIds(matchedVideo)[0] ||
          normalizeId(routeState?.authorId) ||
          "";

        await loadVideosForContext(
          matchedCategoryId,
          "",
          matchedVideo,
          matchedCategoryName
        );

        setSelectedVideo(matchedVideo);
        setPlayingVideoId(getVideoId(matchedVideo));
        setCategoryId(matchedCategoryId || null);
        setSelectedCategoryName(matchedCategoryName || "");
        setAuthorId(matchedAuthorId || null);

      } catch (error) {
        console.error("Error loading videos for landing page:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [routeSlug, sort, i18n.language, videoObjId]);

  useEffect(() => {
    const getAuthors = () => {
      // setLoading(true);
      AuthorsService.getAuthorData(9, null)
        .then((res) => {
          if (res?.status && res?.result) {
            setAuthors(res.result);
          } else {
            setAuthors([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching authors:", error);
          setAuthors([]);
        })
        .finally(() => {
          // setLoading(false);
        });
    };

    getAuthors();
  }, [i18n?.language]);

  useEffect(() => {
    const getCategories = async () => {
      // setLoading(true);

      try {
        const res = await VideoService.getCategoryWiseVideos();
        if (res.result) {
          const categories = res.result;
          const enrichedCategories = await Promise.all(
            categories?.map(async (cat: any) => {
              const subRes = await VideoService.getCategoryWiseVideos(cat.id);
              const subItems = subRes.result[0]?.items || [];
              const subcategories = subItems.filter(
                (item: any) => item.type === 1
              );

              return {
                ...cat,
                items: subcategories,
              };
            })
          );

          setThumbUrls(enrichedCategories);
        } else {
          setThumbUrls([]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setThumbUrls([]);
      } finally {
        // setLoading(false);
      }
    };

    getCategories();
  }, [i18n?.language]);




  const openVideoPlayer = (video: any) => {
    if (!video) return;

    const nextVideoId = getVideoId(video);
    setSelectedVideo(video);
    setPlayingVideoId(nextVideoId);

    const nextCategoryId = getVideoCategoryIds(video)[0] || "";
    const nextCategoryName =
      getVideoCategoryNames(video)[0] || inferVideoSeriesName(video) || "";
    const nextAuthorId = getVideoAuthorIds(video)[0] || "";
    const safeSlug = encodeURIComponent(String(video?.slug || video?.id));

    navigate(`/videos/${safeSlug}`, {
      state: {
        video,
        videoId: video?.id,
        youtubeId: nextVideoId,
        categoryId: nextCategoryId,
        categoryName: nextCategoryName,
        authorId: nextAuthorId,
        from: "VideoLandingPage",
      },
    });
  };

  const handlePlayerReady = (event: any) => {
    event.target.unMute();
    event.target.setVolume(100);
    event.target.playVideo();
  };

  const handleFavouriteClick = async () => {
    try {
      if (isLiked) {
        await
          VideoService.removeFavourite(videoObjId);
        setLikedMsg(false);
      } else {
        // await AudiosService.addFavourite(videoObjId);
        await VideoService.addFavourite(videoObjId)
        setLikedMsg(true);
      }
      setIsLiked(!isLiked);
      setTimeout(() => {
        setLikedMsg(null);
      }, 3000);

    } catch (error) {
      console.error("Error updating favourite:", error);
    }
  };

  const selectedCategory = JSON.parse(
    sessionStorage.getItem("videoSelectedCategory") || "{}"
  );


  return (
    <>

      {

        loading ? <VideoLandingSkeleton /> :
          videosDetail?.slug ? (
            <>
              <VideoSeo
                videos={videosDetail}
                canonicalPath={`/videos/${videosDetail?.slug}`}
              />
            </>
          ) : null}

      <style>{pageStyles}</style>

      <main className="video-page-shell">
        <div style={{ backgroundColor: "rgb(255, 237, 188)", padding: 0 }}>
          <div
            style={{
              width: "100%",
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "40px 10px 16px",
              backgroundColor: "rgb(255, 237, 188)",
            }}
          >
            <div >
              <div style={{ flex: "0 1 260px", minWidth: "220px" }}>
                <div style={{ padding: "8px 0" }}>
                  <h1
                    style={{
                      color: "#cf2c24",
                      fontSize: "40px",
                      margin: 0,
                      fontWeight: 700,
                      textShadow: "0px 0px 4px rgba(255,255,255,0.9)",
                      paddingTop: "10px",
                    }}
                  >
                    {t("Video_tr")}
                  </h1>
                  <div
                    style={{
                      marginTop: "10px",
                      fontSize: 19,
                      fontWeight: 600,
                      color: "rgb(45, 42, 41)",
                    }}
                  >
                    <Link to="/" style={{ color: "#2d2a29" }}>
                      {t("Home_tr")}
                    </Link>
                    <Link to="/videos" style={{ margin: "0 6px", color: "#2d2a29" }}> /
                      <span> {t("Video_tr")}</span>
                    </Link> / {heroVideo?.name}
                  </div>
                </div>
              </div>

              {/* <div
                style={{
                  width: "1px",
                  minHeight: "100px",
                  background: "#facc6f",
                }}
              /> */}

              {/* <div style={{ flex: "1 1 520px", minWidth: "300px" }}>
                <h2
                  style={{
                    textAlign: "center",
                    color: "rgb(228, 121, 7)",
                    textShadow: "0px 0px 4px rgba(255,255,255,0.9)",
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  {t("Videos_title_tr")}
                </h2>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "8px",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ width: "250px", height: "1px", background: "#E5B15A" }} />
                  <svg width="26" height="18" viewBox="0 0 26 18" style={{ margin: "0 12px" }}>
                    <rect x="1" y="7" width="2" height="4" rx="1" fill="#E5B15A" />
                    <rect x="5" y="4" width="2" height="10" rx="1" fill="#E5B15A" />
                    <rect x="9" y="1" width="2" height="16" rx="1" fill="#E5B15A" />
                    <rect x="13" y="4" width="2" height="10" rx="1" fill="#E5B15A" />
                    <rect x="17" y="1" width="2" height="16" rx="1" fill="#E5B15A" />
                    <rect x="21" y="4" width="2" height="10" rx="1" fill="#E5B15A" />
                  </svg>
                  <div style={{ width: "250px", height: "1px", background: "#E5B15A" }} />
                </div>

                <h2
                  style={{
                    fontSize: 17,
                    color: "#4a4036",
                    textShadow: "0px 0px 4px rgba(255,255,255,0.8)",
                    margin: "18px 0px 0px 0px",
                    width: "100%",
                    lineHeight: 1.75,
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    textAlign: "center",
                  }}
                >
                  {t("Videos_description_tr")}
                </h2>
              </div> */}
            </div>
          </div>
        </div>

        <div className="video-page-container" style={{ paddingTop: "20px" }}>
          <div className="video-page-layout">
            <section className="video-main-column">
              {/* <Card className="video-player-card">
                <Card.Body>
                  {playingVideoId ? (
                    <div className="video-frame">
                      <YouTube
                        key={playingVideoId}
                        ref={refVideo}
                        className="youtube-frame"
                        videoId={playingVideoId}
                        opts={youtubeOptions}
                        onReady={handlePlayerReady}
                      />
                    </div>
                  ) : (
                    <img
                      onClick={() => openVideoPlayer(heroVideo)}
                      className="video-thumb"
                      src={getThumbnail(heroVideo)}
                      alt={heroVideo?.name || "Video"}
                    />
                  )}

                  <h1 className="video-title">
                    {heroVideo?.name || (loading ? "Loading videos..." : "No video available")}
                  </h1>
                </Card.Body>
              </Card> */}

              <Card
                className="video-player-card"
                style={{
                  borderRadius: "24px",
                  border: "2px solid #efd2a2",
                  background:
                    "linear-gradient(180deg, #fffaf1 0%, #fff4e2 100%)",
                  overflow: "hidden",
                  boxShadow:
                    "0 12px 35px rgba(184,123,33,0.15), 0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <Card.Body
                  style={{
                    padding: "14px",
                  }}
                >

                  <div
                    style={{
                      position: "relative",
                      display: "inline-block",
                      marginBottom: "12px",
                    }}
                  >
                    <h1
                      style={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#5c2513",
                        margin: 0,
                        padding: "0 20px",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      {heroVideo?.name}
                    </h1>

                    {/* <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        width: "210%",
                        height: "55px",
                        background:
                          "radial-gradient(circle, rgba(255,215,120,.35) 0%, rgba(255,215,120,0) 70%)",
                        zIndex: 1,
                      }}
                    /> */}

                    {/* <span
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "18px",
                        color: "#d4a25a",
                      }}
                    >
                      ❈
                    </span>

                    <span
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "18px",
                        color: "#d4a25a",
                      }}
                    >
                      ❈
                    </span> */}
                  </div>
                  {playingVideoId ? (
                    <div
                      className="video-frame"
                      style={{
                        position: "relative",
                        width: "100%",
                        // aspectRatio: "16/9",
                        aspectRatio: "16/9",
                        overflow: "hidden",
                        borderRadius: "0px",
                        borderTopRightRadius: "12px",
                        borderTopLeftRadius: "12px"
                        // background: "#120008",
                        // border: "1px solid rgba(255,255,255,.4)",
                      }}
                    >
                      <YouTube
                        key={playingVideoId}
                        ref={refVideo}
                        className="youtube-frame"
                        videoId={playingVideoId}
                        opts={youtubeOptions}
                        onReady={handlePlayerReady}
                      />
                    </div>
                  ) : (
                    <img
                      onClick={() => openVideoPlayer(heroVideo)}
                      className="video-thumb"
                      src={getThumbnail(heroVideo)}
                      alt={heroVideo?.name || "Video"}
                      style={{
                        // borderRadius: "18px",
                      }}
                    />
                  )}


                  {/* Vachak + Category Section */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      // background: "#fffaf3",
                      // background:"#fff6e1",
                      // background: "linear-gradient(135deg, #fffaf0 0%, #fff6e1 45%, #ffedc7 100%)",
                      background:
                        "linear-gradient(538deg, #fffcf6 0%, #fff6e1 45%, #ffecc5 100%)",
                      border: "1px solid #E8D1A5",
                      padding: "16px 18px",
                      borderBottomLeftRadius: "12px",
                      borderBottomRightRadius: "12px",
                      marginTop: "-2px",
                    }}
                  >
                    {/* Vachak */}
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      <div
                        style={{
                          width: "55px",
                          height: "55px",
                          borderRadius: "50%",
                          background: "#f7e3df",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {/* <span
                          style={{
                            fontSize: "28px",
                            marginTop: "6px",
                            color: "gray"
                          }}
                        > */}


                        <div
                          style={{
                            padding: "2px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #f7d488 0%, #e8a44d 35%, #ffefc2 70%, #d89b3c 100%)",
                            boxShadow:
                              "0 4px 12px rgba(232,164,77,0.35), 0 0 0 2px rgba(255,239,194,0.4)",
                            display: "inline-flex",
                            marginBottom: "3px",
                          }}
                        >
                          <Avatar
                            alt=""
                            src={heroVideo?.authorThumbPath}
                            sx={{
                              width: 53,
                              height: 53,
                              bgcolor: "#e5deec",
                            }}
                          />
                        </div>
                        {/* {
                      heroVideo?.authorThumbPath ? (
                        <img
                          src={heroVideo.authorThumbPath}
                          alt=""
                          style={{
                            width: "55px",
                            height: "55px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            marginBottom: "8px",
                          }}
                        />
                      ) : (
                        <svg
                          width="55"
                          height="55"
                          viewBox="0 0 24 24"
                          fill="#8B5CF6"
                          style={{ marginBottom: "8px" }}
                        >
                          <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z" />
                        </svg>
                      )
                    } */}

                        {/* </span> */}
                      </div>

                      <div>
                        <div
                          style={{
                            fontSize: "22px",
                            color: "#5c2513",

                            fontWeight: 600,
                            marginBottom: "3px",
                          }}
                        >
                          {t("Authorss_tr")}
                        </div>

                        <div
                          style={{
                            fontSize: "20px",
                            color: "#6d5848",
                            fontWeight: 600,
                            lineHeight: "1.4",
                          }}
                        >
                          {relatedAuthors?.[0]?.name || "-"}
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div
                      style={{
                        width: "1px",
                        height: "65px",
                        background: "#ead5b1",
                        margin: "0 24px",
                      }}
                    />

                    {/* Category */}
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      <div
                        style={{
                          width: "58px",
                          height: "58px",
                          borderRadius: "50%",
                          background: "#fff0d8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: "28px",
                          }}
                        >
                          🎬
                        </span>
                      </div>

                      <div>
                        <div
                          style={{
                            fontSize: "22px",
                            color: "#5c2513",

                            fontWeight: 600,
                            marginBottom: "3px",
                          }}
                        >
                          {t("Category_tr")}
                        </div>

                        <div
                          style={{
                            fontSize: "20px",
                            color: "#6d5848",
                            fontWeight: 600,
                            lineHeight: "1.4",
                          }}
                        >
                          {relatedCategories?.[0]?.name || "-"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", paddingLeft: "8px" }}>
                    <div style={{ marginTop: "5px" }}>
                      {/* <img
                        src={gitasevalogo}
                        style={{
                          height: 50,
                          width: 50,
                          borderRadius: "50%",
                          padding: "5px",
                        }}
                      /> */}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "5px 15px",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontFamily: "ChanakyaUni",
                          fontSize: "22px",
                          marginTop: "8px"
                        }}
                      >
                        {/* {t("Gita_Seva")} */}
                      </div>
                      {likedMsg !== null && (
                        <div
                          style={{
                            textAlign: "end",
                            paddingRight: "10px",
                            fontSize: "20px",
                            fontWeight: 500,
                            color: "#f26d00",
                          }}
                        >
                          {likedMsg ? t("videoLiked_tr") : t("videoDislike_tr")}
                        </div>
                      )}
                      <div>
                        <img
                          src={isLiked ? Favfill : Favempty}
                          onClick={() => {
                            const token = localStorage.getItem("Token");

                            if (token) {
                              handleFavouriteClick();
                            } else {

                              setLogin(true);
                            }
                          }}

                          style={{
                            height: 35,
                            width: 35,
                            cursor: "pointer",
                            marginTop: "5px",
                          }}
                          alt="Favourite Icon"
                          onMouseOver={(e) =>
                            (e.currentTarget.style.transform = "scale(1.2)")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.transform = "scale(1)")
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              {heroVideo?.description && (
                <Card className="video-description-card " style={{
                  boxShadow: "0 12px 35px rgba(184,123,33,0.15), 0 4px 12px rgba(0,0,0,0.08)", borderRadius: "19px",
                  border: "2px solid #efd2a2",
                  background:
                    "linear-gradient(180deg, #fffaf1 0%, #fff4e2 100%)",
                  overflow: "hidden",
                  marginBottom: "20px"
                }}>
                  <Card.Body>
                    <h2 style={{ fontWeight: 600, marginBottom: "10px", fontSize: "22px" }}>
                      {t("Description_tr")}
                    </h2>
                    <h3
                      style={{
                        color: "#666",
                        lineHeight: "28px",
                        marginBottom: 0,
                        fontSize: "18px",
                      }}
                    >
                      {heroVideo.description}
                    </h3>
                  </Card.Body>
                </Card>
              )}

            </section>

            <aside>
              <Card className="related-card"
                style={{
                  marginBottom: "20px",
                  height: heroVideo?.description ? "780px" : "664px",
                  borderRadius: "14px",
                  border: "2px solid #efd2a2",
                  background:
                    "linear-gradient(180deg, #fffaf1 0%, #fff4e2 100%)",
                  overflow: "hidden",
                  boxShadow:
                    "0 12px 35px rgba(184,123,33,0.15), 0 4px 12px rgba(0,0,0,0.08)",

                }} >


                <Card.Body>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>

                    <h2 className="related-title"
                      style={{ fontSize: "20px", color: "#5c2513", }}
                    >
                      {t("Related_Videos_tr")}
                    </h2>
                    <Button
                      // onClick={()=>navigate("/videos",$)}
                      onClick={() =>

                        navigate("/videos", {
                          state: {
                            viewAll: true,
                            categoryId: selectedCategory.categoryId,
                            categoryName: selectedCategory.categoryName,
                          },
                        })

                      }
                      sx={{
                        border: "1px solid #d9a441",
                        borderRadius: "30px",
                        height: "24px",
                        color: "#b27a17",
                        background: "#fff",
                        fontSize: "12px",
                        px: 2.5,
                        py: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#d9a441",
                          color: "#fff",
                        },
                      }}
                      endIcon={<span>→</span>}
                    >
                      <p style={{ paddingTop: 18 }}> View All</p>
                    </Button>
                  </div>

                  <div className="related-list" style={{ maxHeight: 700 }}>
                    {videos.length > 0 ? (
                      videos.map((video: any) => (
                        <div
                          key={video.id || video.slug || getVideoId(video)}
                          onClick={() => openVideoPlayer(video)}
                          className="related-item"
                        >
                          <img
                            src={getThumbnail(video)}
                            alt={video.name}
                            className="related-thumb"
                          />

                          <div className="related-copy">
                            <h3 className="related-video-title">{video.name}</h3>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-related-text">
                        {loading ? "Loading videos..." : "No related videos found"}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </aside>
          </div>
          <LogInModel
            opens={logIn}
            onCloses={closeModal}
            onLoginStateChange={handleLoginStateChange}
          />
        </div>
      </main>
    </>
  );
};

export default VideoLandingPage;
